import { makeAuthCopy } from './copy';
import type { PlatformMeta } from './types';
/**
 * The single source of truth for "what is a platform".
 *
 * Adding a platform means adding one entry here. An `auth` block is present iff
 * the backend has an `/auth/<slug>` route — see functions/resources/passport_auth
 * /index.js, which mounts exactly zoom, youtube, teams and twitch.
 */
const PLATFORM_ENTRIES = {
  all: {
    slug: 'all',
    title: 'All',
    logo: '/images/all_platforms.svg',
    order: 0,
    sandboxOnly: false,
  },
  hybrid: {
    slug: 'hybrid',
    title: 'Hybrid',
    logo: '/images/hybrid.svg',
    order: 8,
    sandboxOnly: false,
  },
  twitch: {
    slug: 'twitch',
    title: 'Twitch',
    shortName: 'Twitch',
    dataNoun: 'streams',
    logo: '/images/twitch-icon.svg',
    order: 6,
    sandboxOrder: 6,
    sandboxOnly: false,
    auth: {
      authPath: 'twitch',
      sendsAppType: false,
      supportsRevoke: false,
      showInSettings: true,
      settingsSection: 'streaming',
      copy: makeAuthCopy('Twitch', {
        inlineBody: 'Find your streams and read chat automatically.',
        body: 'Connecting your Twitch account lets StreamAlive find your streams and read chat automatically.',
        buttonLabel: 'Connect',
      }),
    },
  },
  zoom: {
    slug: 'zoom',
    title: 'Zoom',
    logo: '/images/Zoom.svg',
    socialIcon: '/images/social-icons/zoom.svg',
    order: 1,
    sandboxOrder: 1,
    sandboxOnly: false,
    auth: {
      authPath: 'zoom',
      sendsAppType: true,
      supportsRevoke: true,
      showInSettings: true,
      settingsSection: 'meeting',
      copy: makeAuthCopy('Zoom', {
        header: 'Featured in Zoom App Marketplace',
        headerIcon: 'star',
        inlineBody: 'Join meetings and webinars — no manual registration.',
        body: 'Connecting your Zoom account lets StreamAlive join meetings and webinars — no manual registration.',
        buttonLabel: 'Connect',
        alert: {
          title: 'Let’s make StreamAlive for Zoom effortless!',
          description: 'We’ll handle registrations and other tasks, so you can focus on your audience.',
          buttonLabel: 'Connect',
        },
      }),
    },
  },
  ipa: {
    slug: 'ipa',
    title: 'In-person',
    logo: '/images/in_person.svg',
    order: 7,
    sandboxOrder: 7,
    sandboxOnly: false,
  },
  meet: {
    slug: 'meet',
    title: 'Google Meet',
    logo: '/images/meet-icon.svg',
    socialIcon: '/images/social-icons/meet.svg',
    order: 2,
    sandboxOrder: 2,
    sandboxOnly: false,
  },
  youtube: {
    slug: 'youtube',
    title: 'YouTube',
    marketingName: 'YouTube Live',
    shortName: 'YouTube',
    dataNoun: 'streams',
    logo: '/images/youtube-icon.svg',
    socialIcon: '/images/social-icons/youtube.svg',
    order: 3,
    sandboxOrder: 3,
    sandboxOnly: false,
    auth: {
      authPath: 'youtube',
      sendsAppType: false,
      supportsRevoke: false,
      showInSettings: true,
      settingsSection: 'streaming',
      copy: makeAuthCopy('YouTube', {
        inlineBody: 'Find your scheduled streams and read live chat, with no link copying.',
        body: 'Connecting your YouTube account lets StreamAlive find your scheduled streams and read live chat, with no link copying.',
        helper: 'Opens Google’s sign-in in a new tab; approve it there, then come back here.',
        buttonLabel: 'Connect',
        // The generic template would say "YouTube’s site"; the consent screen is
        // Google's, and the block variant already tells the user so.
        embed: {
          helper: 'Opens Google’s sign-in in your browser; approve it there, then come back here.',
        },
      }),
    },
  },
  teams: {
    slug: 'teams',
    title: 'Microsoft Teams',
    shortName: 'Teams',
    dataNoun: 'meetings',
    logo: '/images/teams-logo.svg',
    socialIcon: '/images/social-icons/teams.svg',
    order: 4,
    sandboxOrder: 4,
    sandboxOnly: false,
    hidden: true,
    auth: {
      authPath: 'teams',
      sendsAppType: false,
      supportsRevoke: false,
      // Teams is still hidden from the platform dropdown, so it stays out of the
      // settings lists too. Authorizing still works from the Presentation form.
      showInSettings: false,
      settingsSection: 'meeting',
      copy: makeAuthCopy('Microsoft Teams', {
        inlineBody: 'Connecting your Microsoft Teams Account lets StreamAlive list your upcoming meetings so you can pick one instead of pasting a link — and you can disconnect it anytime in Settings.',
        body: 'Connecting your Microsoft Teams Account lets StreamAlive list your upcoming meetings so you can pick one instead of pasting a link — and you can disconnect it anytime in Settings.',
        helper: 'Opens Microsoft’s sign-in in a new tab; approve it there, then come back here.',
        buttonLabel: 'Connect my Microsoft Teams account',
        embed: {
          helper: 'Opens Microsoft’s sign-in in your browser; approve it there, then come back here.',
        },
      }),
    },
  },
  teams_web_bot: {
    slug: 'teams_web_bot',
    title: 'Microsoft Teams Bot',
    logo: '/images/teams-logo.svg',
    order: 5,
    sandboxOnly: false,
    hidden: true,
  },
  vimeo: {
    slug: 'vimeo',
    title: 'Vimeo',
    logo: '/images/vimeo-icon.svg',
    order: 6,
    sandboxOnly: false,
    extensionOnly: true,
  },
  webex: {
    slug: 'webex',
    title: 'Webex',
    logo: '/images/webex-icon.svg',
    socialIcon: '/images/social-icons/webex.svg',
    order: 7,
    sandboxOnly: false,
    extensionOnly: true,
  },
  'other-online-events': {
    slug: 'other-online-events',
    title: 'Other online platforms',
    logo: '/images/OtherOnlinePlatforms.svg',
    order: 8,
    sandboxOnly: false,
  },
  linkedin: {
    slug: 'linkedin',
    title: 'LinkedIn',
    marketingName: 'LinkedIn Live',
    logo: '/images/linkedin-icon.svg',
    order: 9,
    sandboxOrder: 11,
    sandboxOnly: true,
    extensionOnly: true,
  },
  streamyard: {
    slug: 'streamyard',
    title: 'Streamyard',
    logo: '/images/streamyard.png',
    order: 10,
    sandboxOrder: 12,
    sandboxOnly: true,
    extensionOnly: true,
  },
  restream: {
    slug: 'restream',
    title: 'Restream',
    logo: '/images/restream.svg',
    socialIcon: '/images/social-icons/restream.svg',
    order: 11,
    sandboxOrder: 9,
    sandboxOnly: true,
    extensionOnly: true,
  },
  airmeet: {
    slug: 'airmeet',
    title: 'Airmeet',
    logo: '/images/airmeet.svg',
    order: 12,
    sandboxOrder: 10,
    sandboxOnly: true,
    extensionOnly: true,
  },
  kick: {
    slug: 'kick',
    title: 'Kick',
    logo: '/images/kick.png',
    order: 13,
    sandboxOrder: 8,
    sandboxOnly: true,
    extensionOnly: true,
  },
  adobe_connect: {
    slug: 'adobe_connect',
    title: 'Adobe Connect',
    logo: '/images/adobe-connect-logo.svg',
    order: 14,
    sandboxOnly: true,
    extensionOnly: true,
  },
  substack: {
    slug: 'substack',
    title: 'Substack',
    logo: '/images/substack.svg',
    order: 15,
    sandboxOnly: true,
    extensionOnly: true,
  },
} satisfies Record<string, PlatformMeta>;

export type PlatformSlug = keyof typeof PLATFORM_ENTRIES;

/**
 * Exported through an explicit `Record<PlatformSlug, PlatformMeta>` rather than
 * as the inferred literal. `satisfies` alone narrows each entry to exactly the
 * keys it declares, so optional fields like `hidden` and `extensionOnly` would
 * not exist on the union — which is what forced the `as any` casts this registry
 * replaces. The key literals survive via PlatformSlug above.
 */
export const PLATFORMS: Record<PlatformSlug, PlatformMeta> = PLATFORM_ENTRIES;

export const hybridPlatformMap = {
  twitch: 'twitch',
  zoom: 'zoom',
  'google meet': 'meet',
  youtube: 'youtube',
  'microsoft teams': 'teams',
  vimeo: 'vimeo',
  webex: 'webex',
  linkedin: 'linkedin',
  'microsoft teams bot': 'teams',
};

export const getGetHybridPlatformSlug = (
  hybridPlatform?: keyof typeof hybridPlatformMap
) => {
  if (!hybridPlatform) return undefined;
  return hybridPlatformMap[hybridPlatform] as PlatformSlug;
};

/**
 * Derived from the registry so a new platform can't be forgotten here. `all` and
 * `hybrid` are meta-entries rather than real platforms, so they stay out.
 */
export const PLATFORM_KEY_TO_DISPLAY_NAME: Record<string, string> = Object.fromEntries(
  Object.values(PLATFORMS)
    .filter((platform) => platform.slug !== 'all')
    .map((platform) => [platform.slug, platform.title])
);

/** Slug -> the short label used in dense UI, falling back to the display name. */
export const PLATFORM_KEY_TO_SHORT_NAME: Record<string, string> = Object.fromEntries(
  Object.values(PLATFORMS)
    .filter((platform) => platform.slug !== 'all')
    .map((platform) => [platform.slug, platform.shortName || platform.title])
);

export const PLATFORM_DISPLAY_NAME_TO_KEY: Record<string, string> = Object.fromEntries(
  Object.entries(PLATFORM_KEY_TO_DISPLAY_NAME).map(([key, display]) => [display, key])
);

/**
 * Platforms StreamAlive can only read through the Chrome extension. For these the
 * session's platform_type slug doubles as the extension's detection key, so a
 * session can be gated on whether the extension reports an open live page.
 */
export const EXTENSION_ONLY_PLATFORMS = new Set(
  Object.values(PLATFORMS)
    .filter((platform) => platform.extensionOnly)
    .map((platform) => platform.slug)
);

/**
 * The "works with" icon strip. Order and labels are deliberate marketing copy and
 * don't all match `title` (e.g. "YouTube Live"), so this stays an explicit list.
 */
export const PLATFORM_IMAGES = [
  { name: 'Zoom', icon: PLATFORMS.zoom.socialIcon },
  { name: 'Microsoft Teams', icon: PLATFORMS.teams.socialIcon },
  { name: 'Google Meet', icon: PLATFORMS.meet.socialIcon },
  { name: 'Webex', icon: PLATFORMS.webex.socialIcon },
  { name: 'YouTube Live', icon: PLATFORMS.youtube.socialIcon },
  { name: 'Restream', icon: PLATFORMS.restream.socialIcon },
];

/**
 * The Sandbox platform dropdown's curated subset, in its own order — deliberately
 * different from the main `order` (it leads with the platforms the demo shows best).
 */
export const SANDBOX_PLATFORMS: Record<string, PlatformMeta> = Object.fromEntries(
  Object.values(PLATFORMS)
    .filter((platform) => platform.sandboxOrder !== undefined)
    .sort((a, b) => a.sandboxOrder! - b.sandboxOrder!)
    .map((platform) => [platform.slug, platform])
);
